// assets/js/components/revenue-calculator/data.js
//
// Reference constants ported verbatim from the spec (assets/files/Revenue
// calculator.pdf, pages 9-20).
//
// Answer VALUES are the untranslated English msgids ("Hair salon", "Other",
// "1 to 2 hours"); only the visible LABEL goes through _t(), via options() below.
// That keeps every comparison in calc.js/data.js and every key in these tables
// valid on all locales, and the backend receives the same English values the
// (English-only) results email prints. Every value here must exist as a msgid
// in translations/js.en.po. Booking-system brand names are proper nouns and are
// never translated.
//
// See ./README.md for the full file responsibility map and exports table.

var BIZ_TYPES = {};
BIZ_TYPES["Beauty & wellness"] = [
    "Hair salon", "Barber shop", "Nail salon", "Spa",
    "Eyelash studio", "Massage & body treatments", "Other beauty & wellness"
];
BIZ_TYPES["Medical & health"] = [
    "Medical clinic", "Doctor", "Dentist", "Psychologist",
    "Chiropractor", "Acupuncturist", "Nutritionist / health coach",
    "Other medical & health"
];
BIZ_TYPES["Fitness & sports"] = [
    "Gym", "Personal trainer", "Yoga / Pilates studio", "Fitness classes",
    "Tennis / padel / squash", "Golf coaching", "Other sports & fitness"
];
BIZ_TYPES["Education & lessons"] = [
    "Tutor", "Teacher / instructor", "School / college",
    "Educational events", "Child care", "Driving school / driving instructor",
    "Other education & lessons"
];
BIZ_TYPES["Professional & public services"] = [
    "Consultant", "Coach", "Counsellor", "Attorney / legal services",
    "Financial services", "Interview scheduling", "Government / public office",
    "Call center", "Other professional services"
];
BIZ_TYPES["Personal services"] = [
    "Pet services", "Cleaning services", "Household services",
    "Design consultations", "Spiritual services", "Other personal services"
];
BIZ_TYPES["Events, rentals & experiences"] = [
    "Photographer", "Event business", "Restaurant bookings", "Escape room",
    "Meeting room rental", "Equipment rental", "Art classes",
    "Other events & experiences"
];
BIZ_TYPES["Other"] = ["Other service business"];

var BOOKING_SYSTEMS = [
    "SimplyBook.me", "Calendly", "Acuity Scheduling", "HubSpot Meetings", "Fresha",
    "Booksy", "Setmore", "Square Appointments", "Mindbody", "Vagaro", "Wix Bookings",
    "YouCanBookMe", "Google Calendar / manual", "Other"
];

var APPT_DUR = {};
APPT_DUR["Beauty & wellness"] = 1;
APPT_DUR["Medical & health"] = 0.5;
APPT_DUR["Fitness & sports"] = 1;
APPT_DUR["Education & lessons"] = 1;
APPT_DUR["Professional & public services"] = 1;
APPT_DUR["Personal services"] = 1;
APPT_DUR["Events, rentals & experiences"] = 1.5;
APPT_DUR["Other"] = 1;

var NS_RATES = {};
NS_RATES["Beauty & wellness"] = 0.15;
NS_RATES["Medical & health"] = 0.12;
NS_RATES["Fitness & sports"] = 0.10;
NS_RATES["Education & lessons"] = 0.10;
NS_RATES["Professional & public services"] = 0.10;
NS_RATES["Personal services"] = 0.10;
NS_RATES["Events, rentals & experiences"] = 0.10;
NS_RATES["Other"] = 0.10;

var HOURS_MAP = {};
HOURS_MAP["Zero hours"] = 0;
HOURS_MAP["Less than 1 hour"] = 0.5;
HOURS_MAP["1 to 2 hours"] = 1.5;
HOURS_MAP["3 to 4 hours"] = 3.5;
HOURS_MAP["5 to 7 hours"] = 6;
HOURS_MAP["8+ hours"] = 9;
var HOURS_OPTS = Object.keys(HOURS_MAP);

var COMP = {
    "Calendly": { h: _t("Calendly is widely used for meeting and consultation scheduling."), b: _t("For service businesses that need revenue-protection features alongside scheduling, SimplyBook.me may be a stronger fit. It includes tools such as booking deposits, automated no-show reminders, cancellation policy enforcement, and a waiting list.") },
    "Acuity Scheduling": { h: _t("Acuity Scheduling covers core booking functionality well for independent service providers."), b: _t("SimplyBook.me may offer more depth in areas like multi-staff management, loyalty programmes, marketing automation, and intake forms.") },
    "HubSpot Meetings": { h: _t("HubSpot Meetings is designed primarily for sales and CRM-driven scheduling workflows."), b: _t("If your priority is running a client-facing service business, SimplyBook.me focuses more directly on appointment management, revenue protection, and client retention.") },
    "Fresha": { h: _t("Fresha is a popular choice in the beauty and wellness sector, offered without a monthly subscription fee."), b: _t("One consideration with Fresha is that it applies a percentage fee on new clients booked through its marketplace. SimplyBook.me uses a flat subscription model with no per-booking commission.") },
    "Booksy": { h: _t("Booksy is a recognised booking platform focused on the beauty and personal care sector."), b: _t("SimplyBook.me may be a stronger fit if you operate outside the beauty sector or want more control over your client acquisition without marketplace dependency.") },
    "Setmore": { h: _t("Setmore provides a straightforward appointment scheduling experience."), b: _t("Compared with Setmore, SimplyBook.me tends to offer more depth in revenue-protection features, including booking deposits, automated reminders, intake forms, and email marketing.") },
    "Square Appointments": { h: _t("Square Appointments is a capable option for businesses already using the Square payments ecosystem."), b: _t("SimplyBook.me supports multiple payment providers and adds tools beyond payment processing, including waitlists, loyalty programmes, and marketing automation.") },
    "Mindbody": { h: _t("Mindbody is a comprehensive platform widely used by fitness studios and wellness businesses."), b: _t("Mindbody pricing is typically designed for larger operations. SimplyBook.me offers comparable core functionality and may be a more cost-accessible option for small to mid-sized businesses.") },
    "Vagaro": { h: _t("Vagaro is a recognised platform serving the beauty, fitness, and wellness sectors."), b: _t("Vagaro's pricing includes additional charges for certain features and scales with the number of staff. SimplyBook.me uses a more transparent pricing structure with a wider feature set available from the start.") },
    "Wix Bookings": { h: _t("Wix Bookings is a convenient option for businesses that already manage their website through Wix."), b: _t("SimplyBook.me is purpose-built for appointment-based businesses and includes more advanced features for staff management, revenue protection, and client communication.") },
    "YouCanBookMe": { h: _t("YouCanBookMe is a practical tool for scheduling meetings and availability-based consultations."), b: _t("If you need a client-facing booking experience with tools such as deposits, cancellation policies, intake forms, and automated reminders, SimplyBook.me may offer a more complete solution.") },
    "Google Calendar / manual": { h: _t("Managing bookings manually or through Google Calendar is a common starting point for many service businesses."), b: _t("As appointment volume grows, manual processes often create scheduling gaps, no-show risk, and time-consuming admin. SimplyBook.me automates the booking flow and adds revenue-protection tools.") }
};

// Select/radio options for a list of msgids: value stays English, label is translated.
function options(values) {
    return values.map(function (v) { return { value: v, label: _t(v) }; });
}

// Visible text for a stored answer value (see the header comment).
function label(value) {
    return value ? _t(value) : "";
}

function getComp(sys) {
    if (!sys || sys === "SimplyBook.me" || sys === "Other" || sys === "") return null;
    return COMP[sys] || null;
}

var UTM_BASE = "utm_source=revenue_health_check&utm_medium=calculator&utm_campaign=revenue_health_check_mvp";

function addUTM(url, content) {
    if (!url) return url;
    var u = UTM_BASE + "&utm_content=" + content;
    var h = url.indexOf("#");
    var base = h === -1 ? url : url.slice(0, h);
    var hash = h === -1 ? "" : url.slice(h);
    return (base.indexOf("?") > -1 ? base + "&" + u : base + "?" + u) + hash;
}

// FL_MAP: feature name -> canonical marketing URL (spec's FL_MAP, page 12).
var FL_MAP = {
    "Automated Reminders": "",
    "Booking Deposits": "https://simplybook.me/en/accept-payments-through-your-booking-page",
    "Cancellation Policy": "",
    "Waiting List": "",
    "Reserve with Google": "https://simplybook.me/en/google-reserve",
    "Instagram & Facebook Booking": "https://simplybook.me/en/accept-bookings-on-website-or-facebook",
    "Promotions and Coupons": "https://simplybook.me/en/promotion-system",
    "Email Marketing": "https://simplybook.me/en/email-templates",
    "SMS Campaigns": "",
    "Review Requests": "https://simplybook.me/en/marketing-tools",
    "Loyalty System": "https://simplybook.me/en/loyalty-system",
    "Memberships": "https://simplybook.me/en/membership-solution",
    "Packages": "https://simplybook.me/en/scheduling-software-with-packages",
    "Service Add-ons": "https://simplybook.me/en/products-sales-and-extras-features",
    "Gift Cards": "https://simplybook.me/en/products-sales-and-extras-features",
    "Intake Forms": "https://simplybook.me/en/intake-forms-soap-medical-history",
    "Calendar Sync": "https://simplybook.me/en/calsync-fixedstart",
    "Multiple Locations": "https://simplybook.me/en/multi-location-booking-system",
    "Multiple Service Providers": ""
};

// SK: recommendation key -> display name (spec's SK, page 12).
var SK = {
    reminders: { name: "Automated Reminders" },
    deposits: { name: "Booking Deposits" },
    cancelPolicy: { name: "Cancellation Policy" },
    waitlist: { name: "Waiting List" },
    reserveGoogle: { name: "Reserve with Google" },
    instagram: { name: "Instagram & Facebook Booking" },
    promotions: { name: "Promotions and Coupons" },
    emailMkt: { name: "Email Marketing" },
    smsMkt: { name: "SMS Campaigns" },
    reviews: { name: "Review Requests" },
    loyalty: { name: "Loyalty System" },
    membership: { name: "Memberships" },
    packages: { name: "Packages" },
    addons: { name: "Service Add-ons" },
    giftCards: { name: "Gift Cards" },
    intakeForms: { name: "Intake Forms" },
    calendarSync: { name: "Calendar Sync" },
    multiLocation: { name: "Multiple Locations" },
    multiStaff: { name: "Multiple Service Providers" }
};

// featureLink: replaces the spec's React <FL fk="..."/> component with a plain
// HTML string builder (no JSX available in this codebase).
function featureLink(key) {
    var f = SK[key];
    if (!f) return "";
    var raw = FL_MAP[f.name] || "";
    if (!raw) return '<strong class="rc-calc__strong">' + _t(f.name) + "</strong>";
    var slug = f.name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    var url = addUTM(raw, "feature_recommendation_" + slug);
    return '<a href="' + url + '" target="_blank" rel="noopener noreferrer" class="rc-calc__link">' + _t(f.name) + "</a>";
}

module.exports = {
    BIZ_TYPES: BIZ_TYPES, BOOKING_SYSTEMS: BOOKING_SYSTEMS, APPT_DUR: APPT_DUR, NS_RATES: NS_RATES,
    HOURS_MAP: HOURS_MAP, HOURS_OPTS: HOURS_OPTS, COMP: COMP, getComp: getComp,
    UTM_BASE: UTM_BASE, addUTM: addUTM, FL_MAP: FL_MAP, SK: SK, featureLink: featureLink,
    options: options, label: label
};
